// Redux
import { AsyncResult, Action, ApiAction, Result } from '..';
import api from '../redux-api';
import { LOGIN } from './auth';

// Types
import { TwoFactorAuthState, TwoFactorEnableSteps } from './types/two-factor-auth';

const INITIAL_STATE: TwoFactorAuthState = {
  qrcode: '',
  secret: '',
  startStep: TwoFactorEnableSteps.BEGIN,
  showModal: false,
  loading: false,
  backup: '',
};

// Actions
const GET_AUTHENTICATORS_NEW: ApiAction = {
  type: 'connect/mfa/new',
  endpoint: 'auth/me/mfa/authenticators/new',
};

const DEACTIVATE_AUTHENTICATORS: ApiAction = {
  type: 'connect/mfa/deactivate',
  endpoint: 'auth/me/mfa/authenticators/deactivate',
};

const POST_AUTHENTICATORS: ApiAction = {
  type: 'connect/mfa/confirmation',
  endpoint: 'auth/me/mfa/authenticators',
};

const POST_BACKUP_CODE: ApiAction = {
  type: 'connect/mfa/backup/new',
  endpoint: 'auth/me/mfa/authenticators/backup',
};

const POST_VERIFY_OTP: ApiAction = {
  type: 'connect/mfa/verify',
  endpoint: 'auth/me/mfa/verify',
};

const SET_TWO_FACTOR_LOADING: Action = {
  type: 'connect/mfa/loading',
};

const SET_SHOW_MODAL: Action = {
  type: 'connect/mfa/showModal',
};

const SET_HIDE_MODAL: Action = {
  type: 'connect/mfa/hideModal',
};

export default function reducer(state: TwoFactorAuthState = INITIAL_STATE, action: Action): TwoFactorAuthState {
  switch (action.type) {
    case SET_TWO_FACTOR_LOADING.type: {
      return {
        ...state,
        loading: true,
      };
    }
    case SET_SHOW_MODAL.type: {
      return {
        ...state,
        showModal: true,
        startStep: action.startStep,
      };
    }
    case SET_HIDE_MODAL.type: {
      return {
        ...state,
        showModal: false,
      };
    }
    case `${GET_AUTHENTICATORS_NEW.type}_SUCCESS`: {
      const { qr, secret } = action.data.data;

      return {
        ...state,
        loading: false,
        qrcode: qr,
        secret,
      };
    }
    case `${POST_AUTHENTICATORS.type}_SUCCESS`: {
      const { backup } = action.data.data;
      return {
        ...state,
        loading: false,
        backup,
      };
    }
    case `${GET_AUTHENTICATORS_NEW.type}_ERROR`:
    case `${POST_AUTHENTICATORS.type}_ERROR`:
    case `${POST_VERIFY_OTP.type}_ERROR`:
    case `${POST_VERIFY_OTP.type}_SUCCESS`: {
      return {
        ...state,
        loading: false,
      };
    }
    case `${DEACTIVATE_AUTHENTICATORS.type}_SUCCESS`: {
      return INITIAL_STATE;
    }
    default:
      return state;
  }
}

export const getNewMFAConfig = (): AsyncResult<void> => dispatch => {
  dispatch({ ...SET_TWO_FACTOR_LOADING });

  return api.get(GET_AUTHENTICATORS_NEW);
};

export const postConfirmationCode =
  (otp: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch({ ...SET_TWO_FACTOR_LOADING });
    const state = getState();
    const {
      twoFactorAuth: { secret },
    } = state;

    const data = {
      otp,
      secret,
    };

    return api.post(POST_AUTHENTICATORS, null, data);
  };

export const verifyOTP =
  (rememberMe: boolean, otp?: string, backup?: string): AsyncResult<void> =>
  dispatch => {
    if (otp === null && backup === null) {
      return Promise.reject();
    }
    dispatch({ ...SET_TWO_FACTOR_LOADING });
    return api.post(POST_VERIFY_OTP, null, { rememberMe, otp, backup }).then((res: { data: any }) => {
      // Dispatch a LOGIN success action to populate the user in Redux.
      dispatch({ type: `${LOGIN.type}_SUCCESS`, data: { data: res.data.user } });

      // Dispatch the action that populates the backup code to be used by the modal
      // and dispatch the action that opens the 2FA modal if the verifyOTP response contains a backup code
      if (res.data.backup) {
        dispatch({ type: `${POST_AUTHENTICATORS.type}_SUCCESS`, data: { data: res.data } });
        dispatch(showModal(TwoFactorEnableSteps.BACKUP_LOGIN));
      }
    });
  };

export const generateBackupCode =
  (otp: string): AsyncResult<void> =>
  dispatch =>
    api.post(POST_BACKUP_CODE, null, { otp }).then((res: { data: any }) => {
      dispatch({ type: `${POST_AUTHENTICATORS.type}_SUCCESS`, data: { data: res.data } });
      dispatch(showModal(TwoFactorEnableSteps.NEW_BACKUP));
    });

export const deleteMFA =
  (otp: string): AsyncResult<void> =>
  () =>
    api.post(DEACTIVATE_AUTHENTICATORS, null, { otp });

export const showModal =
  (startStep = TwoFactorEnableSteps.BEGIN): Result<void> =>
  dispatch => {
    dispatch({ ...SET_SHOW_MODAL, startStep });
  };

export const hideModal = (): Result<void> => dispatch => {
  dispatch({ ...SET_HIDE_MODAL });
};
